import type { Participant, RoomCoreState, RoomPhase, RoomRouting, RoomState, RoutingMode } from './room.types';
import type { GameMode } from './gameMode.types';

// ─── REST ───
export interface CreateRoomRequest {
  hostId: string;
  title?: string;
}

export interface JoinRoomRequest {
  joinCode: string;
  studentId?: string;
  displayName: string;
  identityKey?: string;
}

export interface JoinRoomResponse {
  roomId: string;
  participantId: string;
  sessionToken: string;
  stateVersion: number;
  state: RoomState;
}

// ─── Socket.IO: Host ───
export interface HostJoinRoomPayload {
  roomId: string;
  hostId: string;
}

export interface HostSetPhasePayload {
  roomId: string;
  hostId: string;
  phase: RoomPhase;
}

export interface HostSetActivityPayload {
  roomId: string;
  hostId: string;
  mode: GameMode;
  variant?: string;
  subjectTag?: string;
  templateId?: string;
  payload: unknown;
  runtime?: unknown;
}

export interface HostSetRoutingPayload {
  roomId: string;
  hostId: string;
  mode: RoutingMode;
  teams?: RoomRouting['teams'];
  teamCount?: number;
}

export interface HostAssignTeamsPayload {
  roomId: string;
  hostId: string;
  teams: RoomRouting['teams'];
}

export interface HostEndRoomPayload {
  roomId: string;
  hostId: string;
}

export interface HostLaunchExamPayload {
  roomId: string;
  hostId: string;
  exam: unknown;
}

export interface HostAdvanceExamBlockPayload {
  roomId: string;
  hostId: string;
}

export interface HostEndExamPayload {
  roomId: string;
  hostId: string;
}

export interface HostConfirmExamReviewPayload {
  roomId: string;
  hostId: string;
  participantId: string;
  blockId: string;
}

export interface HostActionPayload {
  roomId: string;
  hostId: string;
  action: string;
  data?: unknown;
}

// ─── Socket.IO: Student ───
export interface StudentJoinRoomPayload {
  roomId?: string;
  joinCode?: string;
  sessionToken?: string;
  participantId?: string;
}

export interface StudentInputPayload {
  roomId: string;
  participantId: string;
  sessionToken?: string;
  input: StudentInputEnvelope;
}

export interface StudentBreachPayload {
  roomId: string;
  participantId: string;
  sessionToken?: string;
  reason?: 'visibility' | 'blur' | 'fullscreen_exit';
}

export interface StudentInputEnvelope {
  gameMode: GameMode;
  action: string;
  data: unknown;
  ts?: number;
}

// ─── Socket.IO: Viewer ───
export interface ViewerJoinRoomPayload {
  roomId: string;
}

// ─── Broadcasts ───
export interface ParticipantsEvent {
  roomId: string;
  participants: Participant[];
  generatedAt: number;
}

export interface StateSyncEvent extends RoomState {}

export interface StateRequestSyncPayload {
  roomId: string;
  lastKnownVersion?: number;
}

// ─── Socket event names ───
export const ROOM_SOCKET_EVENTS = {
  HOST_JOIN: 'host:joinRoom',
  HOST_SET_PHASE: 'host:setPhase',
  HOST_SET_ACTIVITY: 'host:setActivity',
  HOST_SET_ROUTING: 'host:setRouting',
  HOST_ASSIGN_TEAMS: 'host:assignTeams',
  HOST_ACTION: 'host:action',
  HOST_END: 'host:endRoom',
  HOST_SET_ASSESSMENT: 'host:setAssessmentMode',
  HOST_UNLOCK_PARTICIPANT: 'host:unlockParticipant',
  HOST_LAUNCH_EXAM: 'host:launchExam',
  HOST_ADVANCE_EXAM_BLOCK: 'host:advanceExamBlock',
  HOST_END_EXAM: 'host:endExam',
  HOST_CONFIRM_EXAM_REVIEW: 'host:confirmExamReview',
  STUDENT_JOIN: 'student:joinRoom',
  STUDENT_INPUT: 'student:input',
  STUDENT_BREACH: 'student:breach',
  VIEWER_JOIN: 'viewer:joinRoom',
  STATE_SYNC: 'state:sync',
  STATE_PATCH: 'state:patch',
  STATE_REQUEST_SYNC: 'state:requestSync',
  PARTICIPANTS: 'room:participants',
} as const;

export type RoomSocketEvent = (typeof ROOM_SOCKET_EVENTS)[keyof typeof ROOM_SOCKET_EVENTS];
